import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { faMemoCircleInfo } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faChevronDown, faChevronUp, faSidebarFlip } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import salesforceHubspotInstallImage from "~/legacy-assets/images/illustrations/SalesforceHubspotInstall.svg?url";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { SummaryText } from "~/meetingRecorder/SummaryText";
import { openRecapModal } from "~/meetings/MeetingModal/openRecapModal";
import SettingsModal from "~/settings/SettingsModal";
import { Button, useModals } from "~/ui";
import IconButton from "~/ui/legacy/IconButton";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { CompanyPanelContext } from "./CompanyPanelContext";
import { HubspotCard } from "./HubspotCard";
import MetWithEmails from "./MetWithEmails";
import SalesforceCards from "./SalesforceCards";

import type { CompanyPanelLatestPost$data } from "~/__generated__/CompanyPanelLatestPost.graphql";
import type { CompanyPanelQuery, CompanyPanelQuery$data } from "~/__generated__/CompanyPanelQuery.graphql";

import styles from "./CompanyPanel.module.css";

const companyPanelQuery = graphql`
	query CompanyPanelQuery {
		viewer {
			...SettingsModal_viewer
			account {
				billingAccount {
					hasAccessToSalesforceFeature: hasAccessToFeature(feature: salesforce_integration)
					hasAccessToHubspotFeature: hasAccessToFeature(feature: hubspot_integration)
				}
				integrations {
					hubspot {
						userConnected
					}
				}
			}
			integrations {
				salesforce {
					isConnected
				}
			}
		}
	}
`;

graphql`
	fragment CompanyPanelLatestPost on LatestPost {
		id
		postType
		meetingCallStart
		summary
		internalAttendeeNames
	}
`;

export type LatestPost = Omit<NonNullable<CompanyPanelLatestPost$data>, " $fragmentType">;

export function CompanyPanelToggleButton({ onClick }: { onClick: () => void }) {
	return <IconButton icon={faSidebarFlip} onClick={onClick} className={styles.panelToggleButton} />;
}

export function ShowMoreToggle({ isExpanded, onClick }: { isExpanded: boolean; onClick: () => void }) {
	return isExpanded ? (
		<Button className={styles.linkButton} onClick={onClick}>
			Show less <FontAwesomeIcon icon={faChevronUp} size="sm" />
		</Button>
	) : (
		<Button className={styles.linkButton} onClick={onClick}>
			Show more <FontAwesomeIcon icon={faChevronDown} size="sm" />
		</Button>
	);
}

export default function CompanyPanel({
	companyEmails,
	latestMeeting,
}: {
	companyEmails: string[];
	latestMeeting: LatestPost | null;
}) {
	const { viewer } = useLazyLoadQuery<CompanyPanelQuery>(companyPanelQuery, {}, { fetchPolicy: "store-and-network" });

	const {
		account: {
			integrations: { hubspot },
			billingAccount: { hasAccessToSalesforceFeature, hasAccessToHubspotFeature },
		},
		integrations: { salesforce: salesforceIntegration },
	} = viewer;

	const salesforceConnected = salesforceIntegration?.isConnected && hasAccessToSalesforceFeature;
	const hubspotConnected = hubspot?.userConnected && hasAccessToHubspotFeature;

	const [isSummaryOverflowing, setIsSummaryOverflowing] = React.useState(false);
	const [isSummaryExpanded, setIsSummaryExpanded] = React.useState(false);
	const summaryRef = React.useRef<HTMLDivElement | null>(null);

	React.useEffect(() => {
		if (!summaryRef.current) return;
		const summaryIsOverflowing = summaryRef.current?.scrollHeight > summaryRef.current?.clientHeight;
		setIsSummaryOverflowing(summaryIsOverflowing);
	}, [summaryRef, latestMeeting?.summary]);

	const toggleSummaryExpanded = React.useCallback(() => {
		setIsSummaryExpanded(!isSummaryExpanded);
	}, [isSummaryExpanded]);

	const openLatestMeeting = React.useCallback(() => {
		const modalArgs =
			latestMeeting?.postType === "RECORDING" ? { recordingId: latestMeeting?.id } : { noteId: latestMeeting?.id };
		openRecapModal(modalArgs);
	}, [latestMeeting]);

	const { setIsPanelVisible, isPanelVisible } = React.useContext(CompanyPanelContext);
	const closePanel = React.useCallback(() => {
		setIsPanelVisible(false);
	}, [setIsPanelVisible]);

	if (companyEmails.length === 0) {
		return <CompanyPanelEmptyState />;
	}

	const internalAttendeeNames = latestMeeting?.internalAttendeeNames ?? [];

	const loadingFallback = (
		<div className={styles.loadingSpinnerContainer}>
			<LoadingSpinner inline dimmer={false} />
		</div>
	);

	return (
		<div className={cx(styles.panel, { [styles.invisible]: !isPanelVisible })}>
			<div className={styles.panelHeader}>
				<h3>Met with</h3>
				<CompanyPanelToggleButton onClick={closePanel} />
			</div>
			<MetWithEmails companyEmails={companyEmails} />
			<div className={styles.panelSectionHeader}>Latest meeting summary</div>
			{latestMeeting ? (
				<>
					{(latestMeeting.meetingCallStart || internalAttendeeNames.length > 0) && (
						<Button className={styles.linkButton} onClick={openLatestMeeting}>
							{latestMeeting.meetingCallStart &&
								new Date(latestMeeting.meetingCallStart).toLocaleDateString("en-US", {
									month: "short",
									day: "numeric",
								})}
							{internalAttendeeNames.length > 0 && `, with ${internalAttendeeNames.join(", ")}`}
						</Button>
					)}
					<div className={cx(styles.summary, { [styles.collapsed]: !isSummaryExpanded })} ref={summaryRef}>
						<SummaryText>
							{latestMeeting.summary ? latestMeeting.summary : "This meeting wasn't long enough to generate a summary."}
						</SummaryText>
					</div>
				</>
			) : (
				<div className={styles.summary}>No meeting summaries found.</div>
			)}
			{isSummaryOverflowing && <ShowMoreToggle isExpanded={isSummaryExpanded} onClick={toggleSummaryExpanded} />}
			<div className={styles.crmContainer}>
				{salesforceConnected ? (
					<>
						<div className={styles.panelSectionHeader}>Salesforce</div>
						<React.Suspense fallback={loadingFallback}>
							<SalesforceCards emails={companyEmails} />
						</React.Suspense>
					</>
				) : hubspotConnected ? (
					<>
						<div className={styles.panelSectionHeader}>HubSpot</div>
						<React.Suspense fallback={loadingFallback}>
							<HubspotCard emails={companyEmails} />
						</React.Suspense>
					</>
				) : (
					<EmptyCRMState viewer={viewer} />
				)}
			</div>
		</div>
	);
}

/* It's rare to be able to see the company panel with no related data, but it can happen.
 * The list of companies in the filter is the same across all channels, so one case where we need this is when
 * the user applies the company filter to a channel that doesn't contain any meetings with that company.
 */
function CompanyPanelEmptyState() {
	return (
		<div className={cx(styles.emptyStatePanel, styles.panel)}>
			<FontAwesomeIcon icon={faMemoCircleInfo} />
			<p>Nothing to show yet—company details will show up once a meeting happens.</p>
		</div>
	);
}

function EmptyCRMState({ viewer }: { viewer: CompanyPanelQuery$data["viewer"] }) {
	const modals = useModals();

	const openUserSettingsModal = React.useCallback(
		(crmType: "salesforce" | "hubspot") => {
			dispatchAnalyticsTrack("Clicked - Connect CRM CTA", {
				crm: crmType,
				location: "Company filter",
			});
			modals.open(({ close }) => (
				<SettingsModal viewer={viewer} onClose={close} defaultSection={["apps-and-integrations", crmType]} />
			));
		},
		[modals, viewer],
	);

	const openSalesforceSettingsModal = React.useCallback(
		() => openUserSettingsModal("salesforce"),
		[openUserSettingsModal],
	);
	const openHubspotSettingsModal = React.useCallback(() => openUserSettingsModal("hubspot"), [openUserSettingsModal]);

	return (
		<div className={styles.crmContainer}>
			<div className={styles.panelSectionHeader}>CRM integration</div>
			<div className={styles.crmCard}>
				<img src={salesforceHubspotInstallImage} />
				<p>
					Connect your HubSpot or Salesforce account to stay organized, and automatically link your notes, action items,
					and recaps to the right deals and contacts.
				</p>
				<div className={styles.crmButtons}>
					<Button variant="primary" size="slim" onClick={openSalesforceSettingsModal}>
						Connect Salesforce
					</Button>
					<Button variant="primary" size="slim" onClick={openHubspotSettingsModal}>
						Connect HubSpot
					</Button>
				</div>
			</div>
		</div>
	);
}
